// verification-recipe-bash-permission-rules.ts
//
// Dogfood V2.1 lần 2 (09/10, F-2). Worker Bash grants were fixed to
// `Bash(npm test*)` / `Bash(pytest*)` (command-adapter-table.ts), never derived
// from the repo's own approved recipe. A Python repo testing with
// `python3 -m unittest` (no pytest installed) left the testcase worker unable
// to run RED/GREEN; T-0003 was rejected three times for the same ImportError.
//
// A FEATURE ticket whose adapter runs recipes (EVIDENCE_REQUIRED_ADAPTER_IDS)
// now also gets one prefix rule built from its own `verification_recipe` argv
// in `.avatar/verification-profile.json`. Feature tickets dispatch only after
// G2, whose content hash covers that profile — so the command was approved.
// Bootstrap tickets keep the static grants (their profile is not approved yet).
//
// Two rules per recipe: the exact command and the command followed by a SPACE
// and more arguments (review 261010 M1: no word boundary let `npx jest*` match
// `npx jest-evil`). Argv that could still turn that into arbitrary shell is
// refused and reported: shell metacharacters, glob/quote/whitespace inside an
// element (the shell form would not match the joined command anyway), shell
// launchers, and a bare interpreter (`python3` alone ⇒ any script).
import { readFileSync } from "node:fs";
import { basename, join } from "node:path";
import { EVIDENCE_REQUIRED_ADAPTER_IDS } from "./command-adapter-table.js";
import { BOOTSTRAP_TICKET_KINDS } from "./feature-dispatch-gate-guard.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { VerificationProfileSchema } from "./schemas/verification-schema.js";

export const RECIPE_BASH_PROMPT_SECTION_TITLE =
  "## Lệnh recipe được cấp thêm (verification-profile đã duyệt ở G2)";

const UNSAFE_ARGV_CHARS = /[;|&$`<>()*'"\\\s]/;
const LAUNCHERS = new Set([
  "sh",
  "bash",
  "zsh",
  "dash",
  "fish",
  "env",
  "xargs",
  "eval",
  "exec",
  "sudo",
  "nohup",
]);
const INTERPRETERS = new Set(["python", "python3", "node", "ruby", "perl", "php", "deno", "bun"]);

export interface RecipeBashGrants {
  rules: string[];
  /** The granted command (argv joined) and the recipe's `cwd`, for the prompt. */
  command?: string;
  cwd?: string;
  /** One readable line per refused recipe (recipe id + reason) — for the operator and the worker. */
  skipped: string[];
}

export function deriveRecipeBashRule(
  argv: readonly string[],
): { rules: string[] } | { skipReason: string } {
  const head = argv[0];
  if (head === undefined) return { skipReason: "argv rỗng" };
  if (LAUNCHERS.has(basename(head))) {
    return {
      skipReason: `argv[0] "${head}" là trình chạy shell/launcher — cấp quyền sẽ thành Bash tuỳ ý`,
    };
  }
  if (argv.length === 1 && INTERPRETERS.has(basename(head))) {
    return {
      skipReason: `argv chỉ có interpreter "${head}" — cấp quyền sẽ chạy được script bất kỳ`,
    };
  }
  const unsafe = argv.find((element) => element === "" || UNSAFE_ARGV_CHARS.test(element));
  if (unsafe !== undefined) {
    return {
      skipReason: `phần tử argv ${JSON.stringify(unsafe)} chứa ký tự shell/khoảng trắng/rỗng`,
    };
  }
  const command = argv.join(" ");
  return { rules: [`Bash(${command})`, `Bash(${command} *)`] };
}

function readRecipes(
  repoRoot: string,
): Array<{ recipe_id: string; argv: string[]; cwd: string }> | string {
  let raw: string;
  try {
    raw = readFileSync(join(repoRoot, ".avatar", "verification-profile.json"), "utf8");
  } catch {
    return []; // no profile yet: nothing approved to grant
  }
  try {
    return VerificationProfileSchema.parse(JSON.parse(raw)).recipes;
  } catch (err) {
    const detail = err instanceof Error ? err.message.split("\n")[0] : String(err);
    return `.avatar/verification-profile.json không đọc được (${detail})`;
  }
}

/** Grants for this ticket's own approved recipe; no profile/recipe ⇒ none (static rules stay). */
export function loadRecipeBashGrants(repoRoot: string, ticket: Ticket): RecipeBashGrants {
  if (BOOTSTRAP_TICKET_KINDS.includes(ticket.kind)) return { rules: [], skipped: [] };
  if (!EVIDENCE_REQUIRED_ADAPTER_IDS.includes(ticket.command_adapter))
    return { rules: [], skipped: [] };
  const recipes = readRecipes(repoRoot);
  if (typeof recipes === "string") return { rules: [], skipped: [recipes] };
  const recipe = recipes.find((r) => r.recipe_id === ticket.verification_recipe);
  if (recipe === undefined) return { rules: [], skipped: [] };
  const derived = deriveRecipeBashRule(recipe.argv);
  if ("skipReason" in derived) {
    return { rules: [], skipped: [`recipe ${recipe.recipe_id}: ${derived.skipReason}`] };
  }
  return { rules: derived.rules, command: recipe.argv.join(" "), cwd: recipe.cwd, skipped: [] };
}

export function mergeAllowedTools(base: readonly string[], extra: readonly string[]): string[] {
  return [...base, ...extra.filter((rule, i) => !base.includes(rule) && extra.indexOf(rule) === i)];
}

/** Prompt tail telling the worker what it may now run; "" when nothing was granted or refused. */
export function renderRecipeBashPromptSection(grants: RecipeBashGrants): string {
  if (grants.rules.length === 0 && grants.skipped.length === 0) return "";
  // Heading right after the prompt's last line: dropping the section by title
  // (golden I1 E-list) must give back the prompt byte for byte.
  const lines = ["", RECIPE_BASH_PROMPT_SECTION_TITLE];
  if (grants.command !== undefined) {
    const where =
      grants.cwd === undefined || grants.cwd === "." ? "" : ` (chạy trong \`${grants.cwd}\`)`;
    lines.push(
      `- Mục này THAY câu "không được cấp quyền chạy recipe" ở phần output contract: bạn ĐƯỢC chạy \`${grants.command}\`${where} — đúng lệnh recipe của ticket, thêm tham số ở cuối nếu cần (vd chọn 1 file test) — để kiểm RED/GREEN trước khi báo done (rule ${grants.rules.map((r) => `\`${r}\``).join(", ")}). Vẫn KHÔNG ghi report: controller tự chạy recipe và sinh report sau khi nhận result.`,
    );
  }
  for (const line of grants.skipped) {
    lines.push(
      `- KHÔNG cấp lệnh recipe (${line}). Không chạy được ⇒ ghi vào \`remaining_work\`, không đoán kết quả.`,
    );
  }
  return lines.join("\n");
}
